import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { BulkUpsertVariantRow } from 'pages/inquiry-processor/components/family-workspace/variants-grid/editable-variant-row.types';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { masterProductsEndpoint } from './use-get-master-products-api';

const endpoint = `${masterProductsEndpoint}/bulk`;
export const bulkUpsertMasterProductsEndpointIdentifier = endpoint;

export interface BulkUpsertMasterProductsResultDto {
  created: number;
  updated: number;
}

export interface BulkUpsertMasterProductsErrorDto {
  statusCode?: number;
  message: string;
  errors?: string[];
}

type TData = { data: BulkUpsertMasterProductsResultDto };
type TError = AxiosError<BulkUpsertMasterProductsErrorDto>;
type TVariables = { familyId: number; rows: BulkUpsertVariantRow[] };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Bulk create/update one family's variants via POST .../master-products/bulk.
 * Rows with an `id` update that variant; rows without one create a new variant
 * scoped to `familyId`. All-or-nothing: a 400 carries per-row messages in
 * `errors` and nothing is written.
 */
export function useBulkUpsertMasterProductsApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ familyId, rows }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.post(endpoint, { familyId, rows });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: (data: TData, variables: TVariables) => {
      queryClient.invalidateQueries([masterProductsEndpoint, variables.familyId]);
      const { created, updated } = data.data;
      toast({ description: `Saved: ${created} created, ${updated} updated`, status: 'success' });
    },
    ...options,
  });
}
